import { queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { DeviceTracking, DeviceTrackingQueryParams } from '@/api/apis/composer';
import { composerQueryKeys } from './composer.keys';
import { shouldRetryOnError } from '@/queries/case/utils';

export const DEVICE_TRACKING_POLL_INTERVAL_MS = 30_000;
const POLL_BACKOFF_INTERVALS_MS = [10_000, 30_000, 60_000];

export const composerQueries = {
  deviceTracking: (caseId: string) =>
    queryOptions<DeviceTracking>({
      queryKey: composerQueryKeys.getDeviceTracking(caseId),
      queryFn: () => api.composer.getCaseDeviceTracking(caseId),
      retry: shouldRetryOnError,
    }),
  deviceTrackingCustomer: (caseId: string, queryParams?: DeviceTrackingQueryParams) =>
    queryOptions<DeviceTracking>({
      queryKey: [...composerQueryKeys.getDeviceTrackingCustomer(caseId), queryParams],
      queryFn: () => api.composer.getCaseDeviceTrackingCustomer(caseId, queryParams),
      retry: shouldRetryOnError,
    }),
  bulkArrival: (caseIds: string[]) =>
    queryOptions({
      queryKey: composerQueryKeys.getBulkArrival(caseIds),
      queryFn: () => api.composer.getBulkArrival(caseIds),
      retry: shouldRetryOnError,
    }),
  bulkArrivalCustomer: (caseIds: string[]) =>
    queryOptions({
      queryKey: composerQueryKeys.getBulkArrivalCustomer(caseIds),
      queryFn: () => api.composer.getBulkArrivalCustomer(caseIds),
      retry: shouldRetryOnError,
    }),
};

export const useDeviceTrackingQuery = (caseId: string, enabled = true) => {
  return useQuery({
    ...composerQueries.deviceTracking(caseId),
    enabled: Boolean(enabled),
    refetchInterval: (query) => {
      const { fetchFailureCount } = query.state;
      if (fetchFailureCount === 0) return DEVICE_TRACKING_POLL_INTERVAL_MS;
      if (fetchFailureCount > POLL_BACKOFF_INTERVALS_MS.length) return false;
      return POLL_BACKOFF_INTERVALS_MS[fetchFailureCount - 1];
    },
  });
};

export const useDeviceTrackingCustomerQuery = (
  caseId: string,
  enabled = true,
  queryParams?: DeviceTrackingQueryParams,
) => {
  return useQuery({
    ...composerQueries.deviceTrackingCustomer(caseId, queryParams),
    enabled: Boolean(enabled),
    refetchInterval: (query) => {
      const { fetchFailureCount } = query.state;
      if (fetchFailureCount === 0) return DEVICE_TRACKING_POLL_INTERVAL_MS;
      if (fetchFailureCount > POLL_BACKOFF_INTERVALS_MS.length) return false;
      return POLL_BACKOFF_INTERVALS_MS[fetchFailureCount - 1];
    },
  });
};

export const useBulkArrivalQuery = (caseIds: string[], enabled = true) => {
  return useQuery({
    ...composerQueries.bulkArrival(caseIds),
    enabled: Boolean(caseIds.length && enabled),
  });
};

export const useBulkArrivalCustomerQuery = (caseIds: string[], enabled = true) => {
  return useQuery({
    ...composerQueries.bulkArrivalCustomer(caseIds),
    enabled: Boolean(caseIds.length && enabled),
    refetchInterval: (query) => {
      const { fetchFailureCount } = query.state;
      if (fetchFailureCount === 0) return DEVICE_TRACKING_POLL_INTERVAL_MS;
      if (fetchFailureCount > POLL_BACKOFF_INTERVALS_MS.length) return false;
      return POLL_BACKOFF_INTERVALS_MS[fetchFailureCount - 1];
    },
  });
};
